import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  UserAnalytics,
  EngagementMetrics,
  ProductAnalytics,
  BusinessMetrics,
} from '@/models/analytics.model';

const USER_PROFILES_COLLECTION = 'userProfiles';
const RECEIPTS_COLLECTION = 'receipts';
const EMPLOYERS_COLLECTION = 'employers';
const MEMBERS_COLLECTION = 'members';
const PROVIDERS_COLLECTION = 'providers';
const WALLET_LEDGER_COLLECTION = 'walletLedger';

export class AnalyticsService {
  /**
   * Get user analytics
   */
  static async getUserAnalytics(startDate: Date, endDate: Date): Promise<UserAnalytics> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);

    // Get all users
    const usersSnapshot = await getDocs(collection(db, USER_PROFILES_COLLECTION));
    const allUsers = usersSnapshot.docs.map(doc => doc.data());

    // Get new users in period
    const newUsersQuery = query(
      collection(db, USER_PROFILES_COLLECTION),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    );
    const newUsersSnapshot = await getDocs(newUsersQuery);

    // Calculate by role
    const byRole = {
      members: allUsers.filter(u => u.role === 0).length,
      employers: allUsers.filter(u => u.role === 1).length,
      providers: allUsers.filter(u => u.role === 4).length,
      admins: allUsers.filter(u => u.role === 2 || u.role === 3).length,
    };

    // Get active users (users who have logged in or performed actions in the period)
    const activeUsers = allUsers.filter(u => {
      const lastUpdated = u.lastUpdated?.toDate() || u.createdAt?.toDate();
      return lastUpdated >= startDate && lastUpdated <= endDate;
    }).length;

    // Calculate growth trend (simplified - daily counts)
    const growthTrend: Array<{ date: Date; count: number }> = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(23, 59, 59, 999);

      const dayQ = query(
        collection(db, USER_PROFILES_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dayStart)),
        where('createdAt', '<=', Timestamp.fromDate(dayEnd))
      );
      const daySnapshot = await getDocs(dayQ);
      growthTrend.push({
        date: new Date(currentDate),
        count: daySnapshot.docs.length,
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Calculate retention (simplified - would need more data)
    const retentionRate = allUsers.length > 0 ? (activeUsers / allUsers.length) * 100 : 0;

    return {
      totalUsers: allUsers.length,
      activeUsers,
      newUsers: newUsersSnapshot.docs.length,
      retentionRate,
      byRole,
      growthTrend,
    };
  }

  /**
   * Get engagement metrics
   */
  static async getEngagementMetrics(startDate: Date, endDate: Date): Promise<EngagementMetrics> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);

    // Get receipts submitted
    const receiptsQuery = query(
      collection(db, RECEIPTS_COLLECTION),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    );
    const receiptsSnapshot = await getDocs(receiptsQuery);

    // Get reimbursements processed
    const reimbursementsQuery = query(
      collection(db, WALLET_LEDGER_COLLECTION),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('type', '==', 'reimbursement')
    );
    const reimbursementsSnapshot = await getDocs(reimbursementsQuery);

    // Calculate active users (simplified)
    const now = new Date();
    const dailyStart = new Date(now);
    dailyStart.setDate(dailyStart.getDate() - 1);
    const weeklyStart = new Date(now);
    weeklyStart.setDate(weeklyStart.getDate() - 7);
    const monthlyStart = new Date(now);
    monthlyStart.setMonth(monthlyStart.getMonth() - 1);

    const dailyActive = await this.getActiveUsersCount(dailyStart, now);
    const weeklyActive = await this.getActiveUsersCount(weeklyStart, now);
    const monthlyActive = await this.getActiveUsersCount(monthlyStart, now);

    return {
      dailyActiveUsers: dailyActive,
      weeklyActiveUsers: weeklyActive,
      monthlyActiveUsers: monthlyActive,
      averageSessionDuration: 0, // Would need session tracking
      receiptsSubmitted: receiptsSnapshot.docs.length,
      reimbursementsProcessed: reimbursementsSnapshot.docs.length,
      providerSearches: 0, // Would need search tracking
      featureUsage: {}, // Would need feature tracking
    };
  }

  /**
   * Get product analytics
   */
  static async getProductAnalytics(startDate: Date, endDate: Date): Promise<ProductAnalytics> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);

    // Get receipts
    const receiptsQuery = query(
      collection(db, RECEIPTS_COLLECTION),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    );
    const receiptsSnapshot = await getDocs(receiptsQuery);
    const receipts = receiptsSnapshot.docs.map(doc => {
      const data = doc.data() as any;
      return { id: doc.id, ...data };
    });

    // Get reimbursements
    const reimbursementsQuery = query(
      collection(db, WALLET_LEDGER_COLLECTION),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('type', '==', 'reimbursement')
    );
    const reimbursementsSnapshot = await getDocs(reimbursementsQuery);
    const reimbursements = reimbursementsSnapshot.docs.map(doc => {
      const data = doc.data() as any;
      return { id: doc.id, ...data };
    });

    // Calculate receipt stats
    const receiptStats = {
      total: receipts.length,
      pending: receipts.filter(r => {
        const status = r.status;
        return status === 'pending' || status === 0 || (typeof status === 'string' && status.toLowerCase() === 'pending');
      }).length,
      approved: receipts.filter(r => {
        const status = r.status;
        return status === 'approved' || status === 2 || (typeof status === 'string' && status.toLowerCase() === 'approved');
      }).length,
      rejected: receipts.filter(r => {
        const status = r.status;
        return status === 'rejected' || status === 3 || (typeof status === 'string' && status.toLowerCase() === 'rejected');
      }).length,
      averageProcessingTime: 0, // Would need timing data
      byCategory: {} as Record<string, number>,
      trends: [] as Array<{ date: Date; count: number }>,
    };

    // Group by category
    receipts.forEach(r => {
      const category = r.categoryId || 'unknown';
      receiptStats.byCategory[category] = (receiptStats.byCategory[category] || 0) + 1;
    });

    // Calculate reimbursement stats
    const reimbursementStats = {
      total: reimbursements.length,
      totalAmount: reimbursements.reduce((sum, r) => sum + (r.amount || 0), 0),
      averageAmount: reimbursements.length > 0
        ? reimbursements.reduce((sum, r) => sum + (r.amount || 0), 0) / reimbursements.length
        : 0,
      byCategory: {} as Record<string, { count: number; amount: number }>,
      trends: [] as Array<{ date: Date; count: number; amount: number }>,
    };

    // Group reimbursements by category
    reimbursements.forEach(r => {
      const category = r.categoryId || 'unknown';
      if (!reimbursementStats.byCategory[category]) {
        reimbursementStats.byCategory[category] = { count: 0, amount: 0 };
      }
      reimbursementStats.byCategory[category].count++;
      reimbursementStats.byCategory[category].amount += r.amount || 0;
    });

    return {
      receipts: receiptStats,
      reimbursements: reimbursementStats,
      providers: {
        totalSearches: 0, // Would need search tracking
        mostSearched: [],
        byType: {},
      },
      categories: {
        mostUsed: Object.entries(receiptStats.byCategory)
          .map(([categoryId, count]) => ({
            categoryId,
            categoryName: categoryId, // Would need to fetch category names
            usageCount: count,
            totalAmount: 0,
          }))
          .sort((a, b) => b.usageCount - a.usageCount)
          .slice(0, 10),
      },
    };
  }

  /**
   * Get business metrics
   */
  static async getBusinessMetrics(): Promise<BusinessMetrics> {
    const [employersSnapshot, membersSnapshot, providersSnapshot] = await Promise.all([
      getDocs(collection(db, EMPLOYERS_COLLECTION)),
      getDocs(collection(db, MEMBERS_COLLECTION)),
      getDocs(collection(db, PROVIDERS_COLLECTION)),
    ]);

    const employers = employersSnapshot.docs.map(doc => doc.data());
    const members = membersSnapshot.docs.map(doc => doc.data());
    const providers = providersSnapshot.docs.map(doc => doc.data());

    // Calculate current period (this month)
    const now = new Date();
    const currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // Get revenue for current and previous period
    const currentRevenue = await this.getPeriodRevenue(currentStart, now);
    const previousRevenue = await this.getPeriodRevenue(previousStart, previousEnd);

    // Get expenses for current and previous period
    const currentExpenses = await this.getPeriodExpenses(currentStart, now);
    const previousExpenses = await this.getPeriodExpenses(previousStart, previousEnd);

    const revenueGrowth = previousRevenue > 0
      ? ((currentRevenue - previousRevenue) / previousRevenue) * 100
      : 0;
    const expenseGrowth = previousExpenses > 0
      ? ((currentExpenses - previousExpenses) / previousExpenses) * 100
      : 0;

    return {
      totalEmployers: employers.length,
      activeEmployers: employers.filter(e => e.isActive !== false).length,
      totalMembers: members.length,
      activeMembers: members.filter(m => m.status !== 1 && m.status !== 2).length,
      totalProviders: providers.length,
      subscribedProviders: providers.filter(p => p.isSubscribed === true).length,
      revenue: {
        current: currentRevenue,
        previous: previousRevenue,
        growth: revenueGrowth,
      },
      expenses: {
        current: currentExpenses,
        previous: previousExpenses,
        growth: expenseGrowth,
      },
      netIncome: {
        current: currentRevenue - currentExpenses,
        previous: previousRevenue - previousExpenses,
        growth: previousRevenue - previousExpenses > 0
          ? (((currentRevenue - currentExpenses) - (previousRevenue - previousExpenses)) / (previousRevenue - previousExpenses)) * 100
          : 0,
      },
    };
  }

  /**
   * Helper: Get active users count
   */
  private static async getActiveUsersCount(startDate: Date, endDate: Date): Promise<number> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);
    const q = query(
      collection(db, USER_PROFILES_COLLECTION),
      where('lastUpdated', '>=', start),
      where('lastUpdated', '<=', end)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.length;
  }

  /**
   * Helper: Get period revenue
   */
  private static async getPeriodRevenue(startDate: Date, endDate: Date): Promise<number> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);
    const q = query(
      collection(db, 'billingTransactions'),
      where('transactionDate', '>=', start),
      where('transactionDate', '<=', end),
      where('status', '==', 'paid')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.reduce((sum, doc) => {
      const data = doc.data() as any;
      return sum + (data.amount || 0);
    }, 0);
  }

  /**
   * Helper: Get period expenses
   */
  private static async getPeriodExpenses(startDate: Date, endDate: Date): Promise<number> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);
    const q = query(
      collection(db, 'expenses'),
      where('expenseDate', '>=', start),
      where('expenseDate', '<=', end),
      where('status', '==', 'paid')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.reduce((sum, doc) => {
      const data = doc.data() as any;
      return sum + (data.amount || 0);
    }, 0);
  }
}

